import toNumber from 'lodash/toNumber';
import parseBoolean from '../../../utils/parse/parseBoolean';

import {
  MW_LANDING_BYPASS_CLOSEOPTION_X_ENABLED,
  MW_LANDING_DESCRIPTION_ENABLED,
  MW_LANDING_DIRECT_TO_NOW_PLAYING,
  MW_LANDING_DISABLED,
  MW_LANDING_LANDINGONLY_DISABLED,
  MW_LANDING_PLAYBUTTON_ENABLED,
  MW_LANDING_SHOW_AFTER_X_DAYS,
  MW_LANDING_SUBTITLE_ENABLED,
  MW_LANDING_VALUE_PROPOSITION_ENABLED,
  MW_LANDING_VALUE_PROPOSITION_ROOTGENRE_ENABLED,
} from '../../../constants/experiments/mobileLanding';
import {
  COMMERCIAL_FREE_MUSIC_ENABLED,
  FIRSTLAUNCH_UPSELL_DIALOG_ENABLED,
  NEW_LANDING_UPSELL_ENABLED,
  NEW_RIGHTSIDEBAR_UPSELL_ENABLED,
  SUBSEQUENT_LAUNCH_UPSELL_DIALOG_ENABLED,
  SUBSEQUENT_LAUNCH_UPSELL_DIALOG_X_DAYS,
} from '../../../constants/experiments/upsell';
import {
  CLIENT_API_RETRY_LIMIT,
  CLIENT_API_TIMEOUT,
  DISABLE_LANDING_PRODUCT_FETCH,
  DISABLE_RECURLY_PRODUCT_CACHE,
  ENABLE_PROFILE_PLAY_BUTTON_REDIRECT_TO_APP_STORE,
  ENABLE_STATION_PROFILE_RECOMMENDED_CONTENT,
  WEB_GRAPHQL_ENABLED,
  WEB_RECURLY_LIBRARY_TIMEOUT,
  WEB_TUNER_AUTOPLAY_ENABLED,
  WEB_TUNER_INSTREAM_LOGGING_ENABLED,
  WEB_TUNER_SHOUTCAST_EXTRA_TIMEOUT,
  WEB_TUNER_STREAM_RETRY_LIMIT,
  WEB_TUNER_STREAM_TIMEOUT,
} from '../../../constants/experiments/config';
import {
  PLAY_OPEN_IN_APP_ENABLED,
  REDIRECT_TO_HOME_ENABLED,
} from '../../../constants/experiments/redirect';
import {
  BYPASS_UPSELL_DIALOG_ENABLED,
  IN_APP_MESSAGE_DIALOG_DISMISS_EXPIRY,
  TEXT_ME_THE_APP_DISMISS_EXPIRY,
  TEXT_ME_THE_APP_ENABLED,
  TEXT_ME_THE_APP_ENABLED_MAC,
  TEXT_ME_THE_APP_ENABLED_MAC_DESKTOP_APP,
  TEXT_ME_THE_APP_ENABLED_WINDOWS,
  WEB_FIRST_PLAY_INSTALL_APP_DIALOG,
  WEB_OPEN_INSTALL_APP_DIALOG,
  WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER,
  WEB_SUBSEQUENT_LAUNCH_REGISTRATION_ENABLED,
  WEB_SUBSEQUENT_LAUNCH_REGISTRATION_SHOW_IN_DAYS,
} from '../../../constants/experiments/dialog';
import {
  LIVE_DEBOUNCE_DELAY,
  LIVE_RESULTS_ENABLED,
  MINIMUM_CHARACTER_THRESHOLD,
} from '../../../constants/experiments/search';
import {
  ALLOW_IP_ADDRESS_SHARING,
  DATA_OPT_OUT_ENABLED,
  DONT_SELL_MY_DATA_ENABLED,
  MOBILE_PREROLL_ENABLED,
  ONETRUST_ENABLED,
  PAUSE_PREROLL_ENABLED,
  VIDEO_PREROLL_DISABLE_SKIP_KEY,
} from '../../../constants/experiments/ads';
import { AUTH_PAYMENT_FORM_PREVIEW_ENABLED } from '../../../constants/experiments/auth';
import {
  ENABLE_PAYPAL_PAYMENT_METHOD,
  PAYPAL_DISABLED_COUNTRY_LIST,
} from '../../../constants/experiments/payment';
import { REQUIRE_RECAPTCHA } from '../../../constants/auth/recaptcha';
import {
  DESKTOP_NOW_PLAYING_PAGE_ENABLED,
  MAC_DESKTOP_REDIRECT_ALL_TO_HOME,
} from '../../../constants/experiments/desktop';
import {
  METRICS_IFRAME_URL,
  METRICS_WHITELIST,
} from '../../../constants/experiments/metrics';
import { AUTO_DATA_OPT_OUT, SHOW_DATA_OPT_OUT } from '../../../constants/terms';
import {
  OPEN_IN_EMBED_DIALOG_FREQUENCY,
  OPEN_IN_EMBED_ENABLED,
} from '../../../constants/experiments/popout';
import { ALEXA_LINKING_ENABLED } from '../../../constants/experiments/partners';
import {
  ENABLE_PLAYBACK_SPEED_CONTROL,
  IHEARTRADIO_ALBUM_ART_ENABLED,
} from '../../../constants/experiments/player';
import {
  MESSAGE_OF_THE_DAY_COOKIE_EXPIRATION_DAYS,
  MESSAGE_OF_THE_DAY_ENABLED,
} from '../../../constants/dialogs/messageOfTheDay';

function parseArraySetting(setting) {
  return setting.split(',');
}

const config = {
  settingTransforms: {
    'twitterCards.enabledStationIds': parseArraySetting,
    // TODO: Just applying boolean parsing to this for now. Should apply to all keys, but need to
    // make sure it's backwards compatible with mint AND how the keys are parsed within the app.
    'subscription.enabled': parseBoolean,
    [FIRSTLAUNCH_UPSELL_DIALOG_ENABLED]: parseBoolean,
    [MW_LANDING_SUBTITLE_ENABLED]: parseBoolean,
    [MW_LANDING_DESCRIPTION_ENABLED]: parseBoolean,
    [MW_LANDING_VALUE_PROPOSITION_ENABLED]: parseBoolean,
    [MW_LANDING_VALUE_PROPOSITION_ROOTGENRE_ENABLED]: parseBoolean,
    [MW_LANDING_BYPASS_CLOSEOPTION_X_ENABLED]: parseBoolean,
    [MW_LANDING_PLAYBUTTON_ENABLED]: parseBoolean,
    [REDIRECT_TO_HOME_ENABLED]: parseBoolean,
    [CLIENT_API_TIMEOUT]: toNumber,
    [CLIENT_API_RETRY_LIMIT]: toNumber,
    [WEB_TUNER_STREAM_TIMEOUT]: toNumber,
    [WEB_TUNER_SHOUTCAST_EXTRA_TIMEOUT]: toNumber,
    [WEB_TUNER_STREAM_RETRY_LIMIT]: toNumber,
    [WEB_TUNER_INSTREAM_LOGGING_ENABLED]: parseBoolean,
    [COMMERCIAL_FREE_MUSIC_ENABLED]: parseBoolean,
    [BYPASS_UPSELL_DIALOG_ENABLED]: parseBoolean,
    [MW_LANDING_DISABLED]: parseBoolean,
    [MW_LANDING_LANDINGONLY_DISABLED]: parseBoolean,
    [MW_LANDING_DIRECT_TO_NOW_PLAYING]: parseBoolean,
    [PLAY_OPEN_IN_APP_ENABLED]: parseBoolean,
    [WEB_OPEN_INSTALL_APP_DIALOG]: parseBoolean,
    [WEB_FIRST_PLAY_INSTALL_APP_DIALOG]: parseBoolean,
    [WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER]: parseBoolean,
    [LIVE_RESULTS_ENABLED]: parseBoolean,
    [MINIMUM_CHARACTER_THRESHOLD]: toNumber,
    [LIVE_DEBOUNCE_DELAY]: toNumber,
    [VIDEO_PREROLL_DISABLE_SKIP_KEY]: parseBoolean,
    [WEB_RECURLY_LIBRARY_TIMEOUT]: toNumber,
    [DISABLE_RECURLY_PRODUCT_CACHE]: parseBoolean,
    [DISABLE_LANDING_PRODUCT_FETCH]: parseBoolean,
    [ENABLE_PAYPAL_PAYMENT_METHOD]: parseBoolean,
    [AUTH_PAYMENT_FORM_PREVIEW_ENABLED]: parseBoolean,
    [PAYPAL_DISABLED_COUNTRY_LIST]: parseArraySetting,
    [TEXT_ME_THE_APP_ENABLED]: parseBoolean,
    [TEXT_ME_THE_APP_DISMISS_EXPIRY]: toNumber,
    [TEXT_ME_THE_APP_ENABLED_MAC_DESKTOP_APP]: parseBoolean,
    [TEXT_ME_THE_APP_ENABLED_WINDOWS]: parseBoolean,
    [TEXT_ME_THE_APP_ENABLED_MAC]: parseBoolean,
    [REQUIRE_RECAPTCHA]: parseBoolean,
    [MAC_DESKTOP_REDIRECT_ALL_TO_HOME]: parseBoolean,
    [IN_APP_MESSAGE_DIALOG_DISMISS_EXPIRY]: toNumber,
    [PAUSE_PREROLL_ENABLED]: parseBoolean,
    [MOBILE_PREROLL_ENABLED]: parseBoolean,
    [DATA_OPT_OUT_ENABLED]: parseBoolean,
    [DONT_SELL_MY_DATA_ENABLED]: parseBoolean,
    [ALLOW_IP_ADDRESS_SHARING]: parseBoolean,
    [METRICS_WHITELIST]: parseArraySetting,
    [SUBSEQUENT_LAUNCH_UPSELL_DIALOG_ENABLED]: parseBoolean,
    [SUBSEQUENT_LAUNCH_UPSELL_DIALOG_X_DAYS]: toNumber,
    [NEW_LANDING_UPSELL_ENABLED]: parseBoolean,
    [NEW_RIGHTSIDEBAR_UPSELL_ENABLED]: parseBoolean,
    [ENABLE_PROFILE_PLAY_BUTTON_REDIRECT_TO_APP_STORE]: parseBoolean,
    [ENABLE_STATION_PROFILE_RECOMMENDED_CONTENT]: parseBoolean,
    [MW_LANDING_SHOW_AFTER_X_DAYS]: toNumber,
    [OPEN_IN_EMBED_ENABLED]: parseBoolean,
    [OPEN_IN_EMBED_DIALOG_FREQUENCY]: toNumber,
    [ALEXA_LINKING_ENABLED]: parseBoolean,
    [WEB_SUBSEQUENT_LAUNCH_REGISTRATION_ENABLED]: parseBoolean,
    [WEB_SUBSEQUENT_LAUNCH_REGISTRATION_SHOW_IN_DAYS]: toNumber,
    [ONETRUST_ENABLED]: parseBoolean,
    [ENABLE_PLAYBACK_SPEED_CONTROL]: parseBoolean,
    [DESKTOP_NOW_PLAYING_PAGE_ENABLED]: parseBoolean,
    [MESSAGE_OF_THE_DAY_ENABLED]: parseBoolean,
    [MESSAGE_OF_THE_DAY_COOKIE_EXPIRATION_DAYS]: toNumber,
    [WEB_TUNER_AUTOPLAY_ENABLED]: parseBoolean,
    [IHEARTRADIO_ALBUM_ART_ENABLED]: parseBoolean,
    [WEB_GRAPHQL_ENABLED]: parseBoolean,
  },
  termsTransforms: {
    [AUTO_DATA_OPT_OUT]: parseBoolean,
    [SHOW_DATA_OPT_OUT]: parseBoolean,
  },
  blacklistedConfigKeys: [METRICS_WHITELIST, METRICS_IFRAME_URL],
};

export default config;
